/**
 * Guards for URLs that are handed to a navigation sink (window.location.href,
 * window.open, tab.location.href) and are not hardcoded literals.
 *
 * Use these when the destination is legitimately off-site. For post-login
 * targets that must stay on this site use isSafeRedirectTarget in
 * loginUrlUtils.ts instead - the two are not interchangeable.
 */

/** Only these reach a navigation sink; javascript:/data:/vbscript: are the XSS vectors kept out. */
const SAFE_EXTERNAL_PROTOCOLS = ["http:", "https:"];

/**
 * Hosts allowed to receive the signed helpdesk JWT. Freshworks serves the SSO
 * callback from *.myfreshworks.com while the portal itself lives on
 * *.freshdesk.com, so both have to be listed.
 */
const HELPDESK_REDIRECT_DOMAINS = [
  "freshdesk.com",
  "freshworks.com",
  "myfreshworks.com",
];

/**
 * True when url is an absolute http(s) URL, so it is safe to navigate to.
 *
 * The protocol is read off the parsed URL rather than matched as a prefix: the
 * URL parser drops tabs and newlines, so "java\nscript:alert(1)" parses to the
 * javascript: protocol and is refused here instead of slipping past a
 * startsWith check. Relative URLs have no protocol and are refused too - they
 * are not what these sinks are for.
 */
export function isSafeExternalUrl(
  url: string | null | undefined
): url is string {
  if (!url) {
    return false;
  }
  const candidate = url.trim();
  // eslint-disable-next-line no-control-regex
  if (!candidate || /[\u0000-\u001F\u007F]/.test(candidate)) {
    return false;
  }
  try {
    return SAFE_EXTERNAL_PROTOCOLS.includes(new URL(candidate).protocol);
  } catch {
    return false;
  }
}

/**
 * True when redirectUri may receive the helpdesk id_token.
 *
 * The helpdesk callback arrives in the query string (Freshworks lands the user
 * on /jwt?state=..&nonce=..&redirect_uri=..) and the browser is sent on to it
 * carrying the signed helpdesk JWT, so without this check any page linking to
 * /jwt hands that token to a host of its choosing. Accepted are the Freshworks
 * domains and the origin of the org's own configured helpdesk, which covers
 * custom helpdesk domains.
 */
export function isAllowedHelpdeskRedirect(
  redirectUri: string | null | undefined,
  helpdeskBaseUrl?: string | null
): redirectUri is string {
  if (!isSafeExternalUrl(redirectUri)) {
    return false;
  }
  let target: URL;
  try {
    target = new URL(redirectUri.trim());
  } catch {
    return false;
  }
  if (target.protocol !== "https:") {
    return false;
  }
  const host = target.hostname.toLowerCase();
  if (
    HELPDESK_REDIRECT_DOMAINS.some(
      (domain) => host === domain || host.endsWith(`.${domain}`)
    )
  ) {
    return true;
  }
  if (helpdeskBaseUrl) {
    try {
      return new URL(helpdeskBaseUrl).origin === target.origin;
    } catch {
      return false;
    }
  }
  return false;
}
